import type {
  BakedBlockInstance,
  BlockDefinitionMeta,
  Template,
} from '@tsagko/label-contracts'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { formatMm } from '../canvas/format-mm'
import { computeZoneLayout, type LaidOutZone } from '../canvas/layout'
import {
  CollapsibleSection,
  panelBodyId,
  usePanelCollapsed,
} from '../ui/CollapsibleSection'
import { VersionChip } from './VersionChip'
import { computeVersionStatus } from './staleness'

/** Panel id for the collapse state + its localStorage key (#189). */
const PANEL_ID = 'placedBlocks'

interface TemplateBlockInstanceTableProps {
  template: Template
  blockMetas: BlockDefinitionMeta[]
  selectedZoneId?: string
  onSelect: (zone: LaidOutZone) => void
  /** Called when the VersionChip in a table row is clicked. Mirrors the panel chip. */
  onOpenPopover?: (
    baked: BakedBlockInstance,
    path: string[],
    hostBox: { widthMm: number; heightMm: number },
  ) => void
  /** When true, renders an "Update all to latest" button above the table. */
  showBulkUpdate?: boolean
  /** Called when "Update all to latest" is clicked. */
  onBulkUpdateToLatest?: () => void
}

interface InstanceRow {
  zoneId: string
  path: string[]
  baked: BakedBlockInstance
  meta: BlockDefinitionMeta | undefined
  widthMm: number
  heightMm: number
}

function alignmentLabel(p: BakedBlockInstance['placement']): string {
  return `${p.vAlign.charAt(0).toUpperCase()}/${p.hAlign.charAt(0).toUpperCase()}`
}

function collectInstances(
  zones: LaidOutZone[],
  metas: BlockDefinitionMeta[],
): InstanceRow[] {
  const out: InstanceRow[] = []
  for (const z of zones) {
    if (z.bakedDepth !== 0) continue
    if ((z.component as unknown) === undefined) continue
    if (z.component.type !== 'baked-block-instance') continue
    const baked = z.component
    out.push({
      zoneId: z.id,
      path: z.path,
      baked,
      meta: metas.find(m => m.id === baked.blockId),
      widthMm: z.w,
      heightMm: z.h,
    })
  }
  return out
}

export function TemplateBlockInstanceTable({
  template,
  blockMetas,
  selectedZoneId,
  onSelect,
  onOpenPopover,
  showBulkUpdate = false,
  onBulkUpdateToLatest,
}: TemplateBlockInstanceTableProps) {
  const laidOut = useMemo(
    () =>
      computeZoneLayout(template.rootZone, {
        widthMm: template.widthMm,
        heightMm: template.heightMm,
      }),
    [template.rootZone, template.widthMm, template.heightMm],
  )
  const rows = useMemo(() => collectInstances(laidOut, blockMetas), [laidOut, blockMetas])
  const { t } = useTranslation()
  const { collapsed, toggle } = usePanelCollapsed(PANEL_ID)

  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({})
  useEffect(() => {
    if (!selectedZoneId) return
    const row = rowRefs.current[selectedZoneId]
    // jsdom doesn't implement scrollIntoView; guard so tests don't crash.
    if (row && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [selectedZoneId])

  return (
    // The header bar is new (#189) — this panel had nothing to hang a
    // disclosure trigger off. "Update all to latest" moves into it so a stale
    // template can still be bulk-updated while the table is collapsed.
    <CollapsibleSection
      panelId={PANEL_ID}
      title={t('templateDesigner.panels.placedBlocks')}
      collapsed={collapsed}
      onToggle={toggle}
      className="flex shrink-0 flex-col border-b border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900"
      headerClassName="px-2 py-1"
      headerExtra={
        showBulkUpdate ? (
          <button
            type="button"
            data-testid="bulk-update-to-latest"
            onClick={onBulkUpdateToLatest}
            className="rounded border border-sky-400 bg-sky-50 px-2 py-0.5 text-[11px] text-sky-700 hover:bg-sky-100 dark:border-sky-700 dark:bg-sky-950 dark:text-sky-300 dark:hover:bg-sky-900"
          >
            {t('templateDesigner.instanceTable.bulkUpdate')}
          </button>
        ) : null
      }
    >
      {/* #151 contract, verbatim: `max-h-48 shrink-0 overflow-y-auto` is what
          stops a long block list from pushing the canvas off-screen. Pinned
          by TemplateBlockInstanceTable.test.tsx — a failure there is a real
          regression, not test noise. */}
      <div
        id={panelBodyId(PANEL_ID)}
        data-testid="template-block-instance-table"
        className="max-h-48 shrink-0 overflow-y-auto border-t border-stone-200 dark:border-stone-800"
      >
      <table className="w-full text-xs">
        <thead className="bg-stone-50 text-stone-500 dark:bg-stone-950">
          <tr>
            <th className="px-2 py-1 text-left">{t('templateDesigner.instanceTable.colBlock')}</th>
            <th className="px-2 py-1 text-left">{t('templateDesigner.instanceTable.colStatus')}</th>
            <th className="px-2 py-1 text-left">
              {t('templateDesigner.instanceTable.colPlacement')}
            </th>
            <th className="px-2 py-1 text-left">{t('templateDesigner.instanceTable.colSize')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr data-testid="empty-row">
              <td colSpan={4} className="px-2 py-3 text-center text-stone-400">
                {t('templateDesigner.instanceTable.empty')}
              </td>
            </tr>
          )}
          {rows.map(row => {
            const selected = row.zoneId === selectedZoneId
            const status = computeVersionStatus(
              row.baked.blockVersion,
              row.meta?.latestVersion,
              row.meta?.hasDraft ?? false,
            )
            return (
              <tr
                key={row.zoneId}
                ref={el => {
                  rowRefs.current[row.zoneId] = el
                }}
                data-testid="region-row"
                data-zone-id={row.zoneId}
                data-baked-id={row.baked.id}
                onClick={() => {
                  const laid = laidOut.find(z => z.id === row.zoneId)
                  if (laid) onSelect(laid)
                }}
                className={`cursor-pointer ${
                  selected
                    ? 'bg-sky-100 dark:bg-sky-900'
                    : 'hover:bg-stone-50 dark:hover:bg-stone-950'
                }`}
              >
                <td className="px-2 py-1">
                  {(row.meta?.name ?? row.baked.blockId)} · v{row.baked.blockVersion}
                </td>
                <td className="px-2 py-1">
                  <VersionChip
                    status={status}
                    bakedVersion={row.baked.blockVersion}
                    latestVersion={row.meta?.latestVersion}
                    hasDraft={row.meta?.hasDraft ?? false}
                    onClick={() => {
                      onOpenPopover?.(row.baked, row.path, {
                        widthMm: row.widthMm,
                        heightMm: row.heightMm,
                      })
                    }}
                  />
                </td>
                <td className="px-2 py-1 font-mono">{alignmentLabel(row.baked.placement)}</td>
                {/* #126: toFixed(0) rendered a 69 × 7.5 host zone as "69 × 8".
                    formatMm keeps the real value at 1 dp. Separator stays ` × `. */}
                <td className="px-2 py-1 font-mono">
                  {formatMm(row.widthMm)} × {formatMm(row.heightMm)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      </div>
    </CollapsibleSection>
  )
}
